import {
  ArrowDown, ArrowUp, Bot, Braces, Clock3, Copy, File, Filter, GitBranch, GripVertical,
  Image, Link2, Mail, Maximize2, MessageCircle, MousePointer2, Paperclip, Play, Plus,
  Save, Send, Tags, Trash2, Webhook, X, Zap, ZoomIn, ZoomOut,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { createId } from "@/lib/id";

const emptyFunnel = () => ({
  id: null,
  name: "Nowy lejek",
  status: "draft",
  course_id: "",
  trigger: { type: "access_granted" },
  nodes: [],
});

const nodeTypes = [
  ["wait", "Odczekaj", Clock3],
  ["email", "E-mail", Mail],
  ["telegram", "Telegram", Send],
  ["whatsapp", "WhatsApp", MessageCircle],
  ["filter", "Filtr", Filter],
  ["condition", "Rozgałęzienie", GitBranch],
  ["tag", "Tag użytkownika", Tags],
  ["webhook", "Webhook", Webhook],
];

const nodeDefaults = {
  wait: { duration: 1, unit: "days" },
  email: { subject: "Wiadomość od MARSEM", message: "", attachments: [], buttons: [] },
  telegram: { bot_id: "", recipient: "", message: "", attachments: [], buttons: [] },
  whatsapp: { bot_id: "", recipient: "", message: "", attachments: [], buttons: [] },
  condition: { field: "course_progress", operator: "gte", value: "50" },
  filter: { field: "lesson_completed", operator: "is", value: "" },
  tag: { tag: "aktywny", operation: "add" },
  webhook: { url: "https://" },
};

const triggerLabels = {
  access_granted: "Przyznano dostęp",
  lesson_started: "Rozpoczęto lekcję",
  lesson_completed: "Ukończono lekcję",
  form_submitted: "Wysłano formularz",
  course_completed: "Ukończono kurs",
  button_clicked: "Kliknięto przycisk",
  manual: "Start ręczny",
};

const typeMeta = Object.fromEntries(nodeTypes.map(([type, label, Icon]) => [type, { label, Icon }]));
const clampZoom = (value) => Math.max(0.6, Math.min(1.5, Math.round(value * 10) / 10));

export default function FunnelsPage() {
  const [funnels, setFunnels] = useState([]);
  const [courses, setCourses] = useState([]);
  const [bots, setBots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [draggingId, setDraggingId] = useState(null);
  const viewportRef = useRef(null);
  const panRef = useRef(null);

  const load = async (preferredId) => {
    const [{ data: list }, { data: courseList }, { data: botList }] = await Promise.all([
      api.get("/admin/funnels"),
      api.get("/courses"),
      api.get("/admin/bots"),
    ]);
    setFunnels(list);
    setCourses(courseList);
    setBots(botList);
    setSelected((current) => list.find((item) => item.id === (preferredId || current?.id)) || list[0] || emptyFunnel());
  };

  useEffect(() => {
    load().catch(() => toast.error("Nie udało się pobrać lejków"));
  }, []);

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === selected?.course_id),
    [courses, selected?.course_id],
  );
  const patch = (changes) => setSelected((current) => ({ ...current, ...changes }));
  const patchNode = (id, changes) => patch({
    nodes: selected.nodes.map((node) => node.id === id ? { ...node, ...changes } : node),
  });
  const addNode = (type) => patch({
    nodes: [...selected.nodes, {
      id: createId(),
      type,
      name: typeMeta[type]?.label || "Nowy blok",
      ...nodeDefaults[type],
    }],
  });
  const moveNode = (id, direction) => {
    const nodes = [...selected.nodes];
    const from = nodes.findIndex((node) => node.id === id);
    const to = Math.max(0, Math.min(nodes.length - 1, from + direction));
    if (from < 0 || from === to) return;
    const [node] = nodes.splice(from, 1);
    nodes.splice(to, 0, node);
    patch({ nodes });
  };
  const moveNodeTo = (id, targetId) => {
    if (!id || id === targetId) return;
    const nodes = [...selected.nodes];
    const from = nodes.findIndex((node) => node.id === id);
    const to = nodes.findIndex((node) => node.id === targetId);
    if (from < 0 || to < 0) return;
    const [node] = nodes.splice(from, 1);
    nodes.splice(to, 0, node);
    patch({ nodes });
  };
  const duplicateNode = (id) => {
    const index = selected.nodes.findIndex((node) => node.id === id);
    if (index < 0) return;
    const source = selected.nodes[index];
    const duplicate = {
      ...source,
      id: createId(),
      name: `${source.name || typeMeta[source.type]?.label || "Blok"} — kopia`,
      attachments: (source.attachments || []).map((item) => ({ ...item, id: createId() })),
      buttons: (source.buttons || []).map((item) => ({ ...item })),
    };
    const nodes = [...selected.nodes];
    nodes.splice(index + 1, 0, duplicate);
    patch({ nodes });
    toast.success("Blok zduplikowany");
  };

  const save = async () => {
    if (!selected.name.trim()) return toast.error("Podaj nazwę lejka");
    setSaving(true);
    try {
      const payload = {
        name: selected.name,
        status: selected.status,
        course_id: selected.course_id || null,
        trigger: selected.trigger,
        nodes: selected.nodes,
      };
      const { data } = selected.id
        ? await api.put(`/admin/funnels/${selected.id}`, payload)
        : await api.post("/admin/funnels", payload);
      await load(data.id);
      toast.success("Lejek zapisany");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nie udało się zapisać lejka");
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!selected.id || !window.confirm("Usunąć ten lejek?")) return;
    await api.delete(`/admin/funnels/${selected.id}`);
    await load();
    toast.success("Lejek usunięty");
  };
  const test = async () => {
    if (!selected.id) return toast.error("Najpierw zapisz lejek");
    const { data } = await api.post(`/admin/funnels/${selected.id}/test`);
    await load(selected.id);
    toast.success(`Symulacja zakończona: ${data.trace.length} kroków`);
  };
  const beginPan = (event) => {
    if (event.button !== 0 || event.target.closest("button,input,textarea,select,label,.funnel-node,.funnel-trigger-node")) return;
    const viewport = viewportRef.current;
    if (!viewport) return;
    panRef.current = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
    viewport.classList.add("panning");
    viewport.setPointerCapture(event.pointerId);
  };
  const pan = (event) => {
    if (!panRef.current || !viewportRef.current) return;
    viewportRef.current.scrollLeft = panRef.current.left - (event.clientX - panRef.current.x);
    viewportRef.current.scrollTop = panRef.current.top - (event.clientY - panRef.current.y);
  };
  const endPan = (event) => {
    panRef.current = null;
    viewportRef.current?.classList.remove("panning");
    if (viewportRef.current?.hasPointerCapture(event.pointerId)) viewportRef.current.releasePointerCapture(event.pointerId);
  };
  const handleWheel = (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    setZoom((current) => clampZoom(current + (event.deltaY < 0 ? 0.1 : -0.1)));
  };

  if (!selected) return <div className="page loading">Ładowanie lejków…</div>;
  return <div className="funnels-page" data-testid="funnels-page">
    <aside className="funnel-library">
      <header>
        <div><small>AUTOMATYZACJE</small><h1>Lejki</h1></div>
        <button onClick={() => setSelected(emptyFunnel())} aria-label="Nowy lejek"><Plus/></button>
      </header>
      <p>Buduj elastyczne sekwencje reakcji na zachowanie uczniów.</p>
      <div>{funnels.map((funnel) => <button className={selected.id === funnel.id ? "active" : ""} onClick={() => setSelected(funnel)} key={funnel.id}>
        <span className={`funnel-status ${funnel.status}`}/>
        <div><strong>{funnel.name}</strong><small>{triggerLabels[funnel.trigger?.type] || "Zdarzenie"} · {funnel.nodes?.length || 0} kroków</small></div>
        <b>{funnel.stats?.runs || 0}</b>
      </button>)}</div>
    </aside>

    <main className="funnel-workspace">
      <header className="funnel-toolbar">
        <div><span>WIZUALNY BUILDER</span><input value={selected.name} onChange={(event) => patch({ name: event.target.value })} aria-label="Nazwa lejka"/></div>
        <div>
          <select value={selected.status} onChange={(event) => patch({ status: event.target.value })}>
            <option value="draft">Szkic</option><option value="active">Aktywny</option><option value="paused">Wstrzymany</option>
          </select>
          <button onClick={test} disabled={!selected.id}><Play/>TEST</button>
          {selected.id ? <button className="danger" onClick={remove} aria-label="Usuń lejek"><Trash2/></button> : null}
          <button className="save" onClick={save} disabled={saving}><Save/>{saving ? "ZAPIS…" : "ZAPISZ"}</button>
        </div>
      </header>

      <section className="funnel-context-bar">
        <label>KURS<select value={selected.course_id || ""} onChange={(event) => patch({ course_id: event.target.value })}>
          <option value="">Wszystkie kursy</option>{courses.map((course) => <option value={course.id} key={course.id}>{course.title}</option>)}
        </select></label>
        <label>ZDARZENIE STARTOWE<select value={selected.trigger?.type || "access_granted"} onChange={(event) => patch({ trigger: { ...selected.trigger, type: event.target.value } })}>
          {Object.entries(triggerLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </select></label>
        <div><Zap/><span>{selectedCourse ? selectedCourse.title : "Automatyzacja globalna"}</span></div>
      </section>

      <div className="funnel-builder-layout">
        <section className="funnel-map-shell">
          <div className="funnel-map-controls" aria-label="Skala mapy">
            <button onClick={() => setZoom((current) => clampZoom(current - 0.1))} aria-label="Oddal mapę"><ZoomOut/></button>
            <span>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom((current) => clampZoom(current + 0.1))} aria-label="Przybliż mapę"><ZoomIn/></button>
            <button onClick={() => setZoom(1)} aria-label="Przywróć skalę"><Maximize2/></button>
          </div>
          <div
            className="funnel-map-viewport"
            ref={viewportRef}
            onPointerDown={beginPan}
            onPointerMove={pan}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            onWheel={handleWheel}
          >
            <section className="funnel-canvas" aria-label="Łańcuch automatyzacji" style={{ zoom }}>
              <div className="canvas-pan-hint"><MousePointer2/> Przeciągnij tło, aby przesunąć mapę · Ctrl/⌘ + scroll zmienia skalę</div>
              <article className="funnel-trigger-node">
                <span><Zap/></span>
                <div><small>START</small><strong>{triggerLabels[selected.trigger?.type]}</strong><p>{selectedCourse?.title || "Każdy kurs"}</p></div>
              </article>
              {selected.nodes.map((node, index) => {
                const meta = typeMeta[node.type] || { label: node.type, Icon: Braces };
                const Icon = meta.Icon;
                return <div
                  className={`funnel-node-wrap ${draggingId === node.id ? "dragging" : ""}`}
                  key={node.id}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => { event.preventDefault(); moveNodeTo(draggingId, node.id); setDraggingId(null); }}
                >
                  <i/>
                  <article className={`funnel-node type-${node.type}`} data-testid={`funnel-node-${node.id}`}>
                    <header>
                      <button
                        className="node-drag-handle"
                        draggable
                        onDragStart={(event) => { setDraggingId(node.id); event.dataTransfer.effectAllowed = "move"; }}
                        onDragEnd={() => setDraggingId(null)}
                        aria-label="Przeciągnij blok"
                      ><GripVertical/></button>
                      <span><Icon/></span>
                      <div className="node-title">
                        <small>{meta.label} · KROK {index + 1}</small>
                        <input value={node.name || meta.label} onChange={(event) => patchNode(node.id, { name: event.target.value })} aria-label="Nazwa bloku"/>
                      </div>
                      <div className="node-actions">
                        <button onClick={() => moveNode(node.id, -1)} disabled={index === 0} aria-label="Przenieś wyżej"><ArrowUp/></button>
                        <button onClick={() => moveNode(node.id, 1)} disabled={index === selected.nodes.length - 1} aria-label="Przenieś niżej"><ArrowDown/></button>
                        <button onClick={() => duplicateNode(node.id)} aria-label="Duplikuj blok"><Copy/></button>
                        <button onClick={() => patch({ nodes: selected.nodes.filter((item) => item.id !== node.id) })} aria-label="Usuń blok"><Trash2/></button>
                      </div>
                    </header>
                    <NodeFields node={node} patch={(changes) => patchNode(node.id, changes)} bots={bots} course={selectedCourse}/>
                  </article>
                </div>;
              })}
              <div className="funnel-end"><i/><span>KONIEC ŚCIEŻKI</span></div>
            </section>
          </div>
        </section>

        <aside className="funnel-palette">
          <small>DODAJ KOLEJNY KROK</small>
          {nodeTypes.map(([type, label, Icon]) => <button onClick={() => addNode(type)} key={type}>
            <span><Icon/></span>
            <div><strong>{label}</strong><small>{type === "wait" ? "Opóźnij dalsze kroki" : type === "filter" ? "Przepuść pasujących uczniów" : type === "condition" ? "Rozdziel ścieżkę warunkiem" : "Dodaj akcję do sekwencji"}</small></div>
            <Plus/>
          </button>)}
          <div className="funnel-help">
            <Bot/><strong>Zmienne ucznia</strong>
            <p>W treści używaj: <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code>, <code>{"{{course}}"}</code> lub własnych zmiennych z karty klienta.</p>
          </div>
        </aside>
      </div>
    </main>
  </div>;
}

function NodeFields({ node, patch, bots, course }) {
  if (node.type === "wait") return <div className="node-fields inline">
    <label>ILE<input type="number" min="1" value={node.duration || 1} onChange={(event) => patch({ duration: Number(event.target.value) })}/></label>
    <label>JEDNOSTKA<select value={node.unit || "days"} onChange={(event) => patch({ unit: event.target.value })}>
      <option value="minutes">Minuty</option><option value="hours">Godziny</option><option value="days">Dni</option>
    </select></label>
  </div>;
  if (node.type === "email") return <div className="node-fields">
    <label>TEMAT<input value={node.subject || ""} onChange={(event) => patch({ subject: event.target.value })}/></label>
    <RichContentEditor node={node} patch={patch}/>
  </div>;
  if (["telegram", "whatsapp"].includes(node.type)) return <div className="node-fields">
    <div className="inline">
      <label>BOT<select value={node.bot_id || ""} onChange={(event) => patch({ bot_id: event.target.value })}>
        <option value="">Wybierz bota</option>{bots.filter((bot) => bot.channel === node.type).map((bot) => <option value={bot.id} key={bot.id}>{bot.name}</option>)}
      </select></label>
      <label>ODBIORCA<input value={node.recipient || ""} onChange={(event) => patch({ recipient: event.target.value })} placeholder="Domyślny z bota"/></label>
    </div>
    <RichContentEditor node={node} patch={patch}/>
  </div>;
  if (["condition", "filter"].includes(node.type)) return <FilterFields node={node} patch={patch} course={course}/>;
  if (node.type === "tag") return <div className="node-fields inline">
    <label>OPERACJA<select value={node.operation || "add"} onChange={(event) => patch({ operation: event.target.value })}><option value="add">Dodaj tag</option><option value="remove">Usuń tag</option></select></label>
    <label>NAZWA TAGU<input value={node.tag || ""} onChange={(event) => patch({ tag: event.target.value })}/></label>
  </div>;
  return <div className="node-fields"><label>ADRES WEBHOOKA<input value={node.url || ""} onChange={(event) => patch({ url: event.target.value })} placeholder="https://"/></label></div>;
}

function FilterFields({ node, patch, course }) {
  const lessons = (course?.modules || []).flatMap((module) => (module.lessons || []).map((lesson) => ({
    ...lesson,
    moduleTitle: module.title,
  })));
  const field = node.field || "course_progress";
  const lessonField = ["lesson_completed", "lesson_not_completed", "form_submitted"].includes(field);
  const timeField = field === "days_inactive";
  const numberField = ["course_progress", "days_inactive"].includes(field);
  const textField = ["has_tag", "variable"].includes(field);
  return <div className="node-fields filter-fields">
    <div className="inline">
      <label>SPRAWDŹ<select value={field} onChange={(event) => patch({ field: event.target.value, value: "", operator: ["lesson_completed", "lesson_not_completed", "form_submitted", "course_completed"].includes(event.target.value) ? "is" : "gte" })}>
        <option value="lesson_completed">Ukończył lekcję</option>
        <option value="lesson_not_completed">Nie ukończył lekcji</option>
        <option value="course_progress">Postęp kursu</option>
        <option value="course_completed">Ukończył kurs</option>
        <option value="days_inactive">Nie logował się przez</option>
        <option value="form_submitted">Wysłał formularz</option>
        <option value="has_tag">Ma tag</option>
        <option value="variable">Wartość zmiennej</option>
      </select></label>
      {numberField ? <label>WARUNEK<select value={node.operator || "gte"} onChange={(event) => patch({ operator: event.target.value })}>
        <option value="gte">Co najmniej</option><option value="lte">Nie więcej niż</option><option value="eq">Dokładnie</option>
      </select></label> : null}
      {textField ? <label>WARUNEK<select value={node.operator || "eq"} onChange={(event) => patch({ operator: event.target.value })}>
        <option value="eq">Jest równe</option><option value="neq">Nie jest równe</option><option value="contains">Zawiera</option>
      </select></label> : null}
    </div>
    {lessonField ? <label>LEKCJA<select value={node.value || ""} onChange={(event) => patch({ value: event.target.value })}>
      <option value="">Wybierz lekcję</option>
      {lessons.filter((lesson) => field !== "form_submitted" || lesson.custom_form?.enabled).map((lesson) => <option value={lesson.id} key={lesson.id}>{lesson.moduleTitle} · {lesson.title}</option>)}
    </select></label> : null}
    {field === "course_progress" ? <label>PROCENT<input type="number" min="0" max="100" value={node.value || ""} onChange={(event) => patch({ value: event.target.value })} placeholder="50"/></label> : null}
    {timeField ? <div className="inline"><label>CZAS<input type="number" min="1" value={node.value || ""} onChange={(event) => patch({ value: event.target.value })} placeholder="7"/></label><label>JEDNOSTKA<select value={node.unit || "days"} onChange={(event) => patch({ unit: event.target.value })}><option value="hours">Godzin</option><option value="days">Dni</option><option value="weeks">Tygodni</option></select></label></div> : null}
    {field === "has_tag" ? <label>TAG<input value={node.value || ""} onChange={(event) => patch({ value: event.target.value })} placeholder="np. VIP"/></label> : null}
    {field === "variable" ? <div className="inline"><label>ZMIENNA<input value={node.variable_key || ""} onChange={(event) => patch({ variable_key: event.target.value })} placeholder="np. plan"/></label><label>WARTOŚĆ<input value={node.value || ""} onChange={(event) => patch({ value: event.target.value })}/></label></div> : null}
    {field === "course_completed" ? <p className="filter-summary">Przepuści tylko osoby, które ukończyły 100% wybranego kursu.</p> : null}
  </div>;
}

function RichContentEditor({ node, patch }) {
  const textareaRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const attachments = node.attachments || [];
  const buttons = node.buttons || [];
  const insertToken = (token, changes = {}) => {
    const message = node.message || "";
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? message.length;
    const end = textarea?.selectionEnd ?? start;
    const spacerBefore = start && message[start - 1] !== "\n" ? "\n" : "";
    const spacerAfter = end < message.length && message[end] !== "\n" ? "\n" : "";
    patch({ ...changes, message: `${message.slice(0, start)}${spacerBefore}${token}${spacerAfter}${message.slice(end)}` });
    requestAnimationFrame(() => textareaRef.current?.focus());
  };
  const upload = async (event, kind) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;
    const form = new FormData();
    form.append("file", uploadedFile);
    setUploading(true);
    try {
      const { data } = await api.post("/files", form);
      const attachment = { id: createId(), file_id: data.id, name: uploadedFile.name, kind };
      insertToken(`{{${kind}:${data.id}}}`, { attachments: [...attachments, attachment] });
      toast.success(kind === "image" ? "Obraz dodany do wiadomości" : "Plik dołączony");
    } catch {
      toast.error("Nie udało się przesłać pliku");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };
  const addButton = () => {
    const button = { id: createId(), label: "Przejdź dalej", url: "https://" };
    insertToken(`{{button:${button.id}}}`, { buttons: [...buttons, button] });
  };
  const removeAttachment = (attachment) => patch({
    attachments: attachments.filter((item) => item.id !== attachment.id),
    message: (node.message || "").replaceAll(`{{${attachment.kind}:${attachment.file_id}}}`, ""),
  });
  const removeButton = (button) => patch({
    buttons: buttons.filter((item) => item.id !== button.id),
    message: (node.message || "").replaceAll(`{{button:${button.id}}}`, ""),
  });
  return <div className="rich-message-editor">
    <div className="message-editor-toolbar">
      <span>TREŚĆ WIADOMOŚCI</span>
      <label className={uploading ? "disabled" : ""}><Image/> Obraz<input type="file" accept="image/*" disabled={uploading} onChange={(event) => upload(event, "image")}/></label>
      <label className={uploading ? "disabled" : ""}><Paperclip/> Plik<input type="file" disabled={uploading} onChange={(event) => upload(event, "file")}/></label>
      <button type="button" onClick={addButton}><Link2/> Przycisk</button>
    </div>
    <textarea ref={textareaRef} value={node.message || ""} onChange={(event) => patch({ message: event.target.value })} placeholder="Napisz wiadomość i wstaw multimedia lub przycisk w wybranym miejscu…"/>
    {attachments.length ? <div className="message-attachments">{attachments.map((attachment) => <span key={attachment.id}>{attachment.kind === "image" ? <Image/> : <File/>}<b>{attachment.name}</b><button onClick={() => removeAttachment(attachment)} aria-label="Usuń załącznik"><X/></button></span>)}</div> : null}
    {buttons.length ? <div className="message-buttons">{buttons.map((button) => <div key={button.id}><Link2/><input value={button.label} onChange={(event) => patch({ buttons: buttons.map((item) => item.id === button.id ? { ...item, label: event.target.value } : item) })} aria-label="Tekst przycisku"/><input value={button.url} onChange={(event) => patch({ buttons: buttons.map((item) => item.id === button.id ? { ...item, url: event.target.value } : item) })} placeholder="https://" aria-label="Adres przycisku"/><button onClick={() => removeButton(button)} aria-label="Usuń przycisk"><Trash2/></button></div>)}</div> : null}
  </div>;
}
